import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import type { Hub, HubCommand } from '../../../domain/Hub';
import type { Site } from '../../../domain/Site';

const COMMAND_SENT: Record<HubCommand, string> = {
  emparejar: 'El hub está buscando estaciones nuevas.',
  reiniciar: 'El hub se está reiniciando: vuelve en menos de un minuto.',
  wifi: 'El hub levantó su red de instalación por 5 minutos: escaneá "Conectar" en la etiqueta.',
};

// El hub como "conexión del campo": su estado, en qué campo está, sus
// estaciones y lo que se le puede pedir sin abrir la caja.
export function useHubDetail(hubId: string) {
  const history = useHistory();
  const { hubs: hubsRepo, devices: devicesRepo, sites: sitesRepo } = useRepositories();
  const [hub, setHub] = useState<Hub | null>(null);
  const [stations, setStations] = useState<Device[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [{ hubs }, { devices }, { sites }] = await Promise.all([hubsRepo.list(), devicesRepo.list(), sitesRepo.list()]);
      const found = hubs.find((h) => h.id === hubId) ?? null;
      setHub(found);
      if (!found) setError('Este hub no está vinculado a tu cuenta.');
      setStations(devices.filter((d) => d.hubId === hubId));
      setSites(sites);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el hub.');
    } finally {
      setLoading(false);
    }
  }, [hubId, hubsRepo, devicesRepo, sitesRepo]);

  useIonViewWillEnter(() => {
    load();
  });

  const changeSite = async (siteId: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await hubsRepo.setSite(hubId, siteId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar el campo.');
    } finally {
      setBusy(false);
    }
  };

  const sendCommand = async (command: HubCommand) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const { hubOnline } = await hubsRepo.command(hubId, command);
      // Las órdenes no quedan guardadas: si el hub no está conectado, no le llegan.
      setMessage(
        hubOnline ? COMMAND_SENT[command] : 'El hub está sin conexión, así que la orden no le llegó. Revisá que tenga luz e internet.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo mandar la orden.');
    } finally {
      setBusy(false);
    }
  };

  return {
    hub,
    stations,
    sites,
    loading,
    error,
    message,
    busy,
    changeSite,
    restart: () => sendCommand('reiniciar'),
    openWifiSetup: () => sendCommand('wifi'),
    addStation: () => history.push(`/app/estaciones/nueva?hub=${hubId}`),
    goToStation: (id: string) => history.push(`/app/stations/${id}`),
  };
}
